import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { FileEdit, GitBranch, Loader2, Trash2 } from "lucide-react"
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { databases } from "@/lib/appwrite"
import { Query } from "appwrite"
import { getDraftsByInstrument } from "@/Wist-blockchain/collections/serviceReport/getAllServiceReport"
import type { ServiceReport } from "@/Wist-blockchain/collections/serviceReport/types"
import { toast } from "react-toastify"

const DB = import.meta.env.VITE_BLOCKCHAIN_DATABASE_ID
const COL_REPORT = import.meta.env.VITE_BLOCKCHAIN_SERVICE_REPORT_COLLECTION_ID
const COL_ACTS = import.meta.env.VITE_BLOCKCHAIN_REPORT_ACTIVITIES_COLLECTION_ID
const COL_MATS = import.meta.env.VITE_BLOCKCHAIN_REPORT_MATERIALS_COLLECTION_ID

interface Props {
    instrumentId: string
}

function DraftItem({ draft, instrumentId }: { draft: ServiceReport; instrumentId: string }) {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [isDeleting, setIsDeleting] = useState(false)
    const isRevision = !!draft.parentReportId

    const handleContinue = () => {
        navigate({
            to: '/instruments/serviceReport/$id/$draftId',
            params: { id: instrumentId, draftId: draft.$id },
        })
    }

    const handleDelete = async () => {
        setIsDeleting(true)
        try {
            const [acts, mats] = await Promise.all([
                databases.listDocuments(DB, COL_ACTS, [Query.equal("idReport", draft.idReport)]),
                databases.listDocuments(DB, COL_MATS, [Query.equal("idReport", draft.idReport)]),
            ])
            await Promise.all([
                ...acts.documents.map(d => databases.deleteDocument(DB, COL_ACTS, d.$id)),
                ...mats.documents.map(d => databases.deleteDocument(DB, COL_MATS, d.$id)),
            ])
            await databases.deleteDocument(DB, COL_REPORT, draft.$id)
            queryClient.invalidateQueries({ queryKey: ["drafts", instrumentId] })
            toast.success("Borrador eliminado.")
        } catch {
            toast.error("Error al eliminar el borrador.")
        } finally {
            setIsDeleting(false)
        }
    }

    const color = isRevision
        ? { border: "border-amber-200 dark:border-amber-800", bg: "bg-amber-50 dark:bg-amber-950/30", icon: "bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400", text: "text-amber-800 dark:text-amber-300", sub: "text-amber-600 dark:text-amber-400", btn: "text-amber-700 dark:text-amber-300" }
        : { border: "border-blue-200 dark:border-blue-800", bg: "bg-blue-50 dark:bg-blue-950/30", icon: "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400", text: "text-blue-800 dark:text-blue-300", sub: "text-blue-600 dark:text-blue-400", btn: "text-blue-700 dark:text-blue-300" }

    return (
        <div className={`flex items-center justify-between gap-3 rounded-lg border ${color.border} ${color.bg} px-4 py-3`}>
            {/* Info */}
            <div className="flex items-center gap-3 min-w-0">
                <div className={`flex h-8 w-8 items-center justify-center rounded-md shrink-0 ${color.icon}`}>
                    {isRevision ? <GitBranch className="h-4 w-4" /> : <FileEdit className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <p className={`text-sm font-semibold ${color.text}`}>
                            {isRevision ? "Borrador de revisión pendiente" : "Borrador pendiente"}
                        </p>
                        {isRevision && (
                            <span className="text-[10px] font-bold bg-amber-200 text-amber-800 rounded px-1 py-0.5">
                                REV{draft.revisionNumber}
                            </span>
                        )}
                    </div>
                    <p className={`text-xs font-mono truncate ${color.sub}`}>
                        {isRevision ? `Base: ${draft.parentReportId}` : draft.idReport}
                    </p>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 shrink-0">
                <button
                    type="button"
                    onClick={handleContinue}
                    className={`text-xs font-semibold hover:underline cursor-pointer ${color.btn}`}
                >
                    Continuar →
                </button>

                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button
                            type="button"
                            disabled={isDeleting}
                            className="flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Eliminar borrador"
                        >
                            {isDeleting
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />
                            }
                        </button>
                    </AlertDialogTrigger>

                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <div className="flex items-center gap-3 mb-1">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40 shrink-0">
                                    <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                                </div>
                                <AlertDialogTitle>¿Eliminar borrador?</AlertDialogTitle>
                            </div>
                            <AlertDialogDescription>
                                Esta acción es <span className="font-semibold text-foreground">permanente e irreversible</span>.
                                El borrador{isRevision ? " de revisión" : ""}{" "}
                                <span className="font-mono text-xs">{draft.idReport}</span> y todos los datos relacionados serán eliminados y no podrán recuperarse.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="mt-2">
                            <AlertDialogCancel className="cursor-pointer">Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="cursor-pointer bg-red-600 hover:bg-red-700 text-white gap-1.5"
                            >
                                <Trash2 className="h-4 w-4" />
                                Sí, eliminar
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    )
}

export const DraftBanner = ({ instrumentId }: Props) => {
    const { data: drafts = [], isLoading } = useQuery({
        queryKey: ["drafts", instrumentId],
        queryFn: () => getDraftsByInstrument(instrumentId),
    })

    if (isLoading) return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Verificando borradores...
        </div>
    )

    if (drafts.length === 0) return null

    return (
        <div className="space-y-2">
            {drafts.map((draft) => (
                <DraftItem key={draft.$id} draft={draft} instrumentId={instrumentId} />
            ))}
        </div>
    )
}
